import { type QueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

import { correspondenceKeys } from '../correspondence/correspondence-query-keys';
import {
  invalidateRevocationListCaches,
  isSharedAccountId,
  removeCorrespondenceAccessCaches,
} from './correspondence-share-access';

import type { Database } from '@/integrations/supabase/types';

/**
 * Catches share revocations and downgrades whose realtime event never arrived
 * (socket down, tab asleep, event dropped). Every trusted revoke path, member
 * removal and mailbox disconnect included, soft-revokes the share row, so the
 * person's active shares, read again and compared with the last read, show each
 * access that ended. See
 * docs/plans/2026-10-30-fix-correspondence-sharing-audit-plan.md (U3, F3).
 */

export type SharePermission = Database['public']['Enums']['share_permission_level'];

/** Plain rows, not a Map: the snapshot is persisted with the query cache. */
export interface ShareSnapshotRow {
  readonly correspondence_id: string;
  readonly permission: SharePermission;
  readonly email_account_id: string | null;
}

export type ShareSnapshot = readonly ShareSnapshotRow[];

export const SHARE_RECONCILE_INTERVAL_MS = 60_000;

/** Below PostgREST's max_rows, so a full page always means "read the next one". */
const SNAPSHOT_PAGE_SIZE = 500;

const PERMISSION_RANK: Readonly<Record<SharePermission, number>> = {
  view: 0,
  comment: 1,
  draft: 2,
  send: 3,
};

export const shareSnapshotKey = (organizationId: string, userId: string) =>
  [...correspondenceKeys.all, 'share-snapshot', organizationId, userId] as const;

/**
 * Reads every active share, page by page after the last id seen (keyset, so a
 * revoke between pages cannot shift a live share out of the read); any failed
 * page fails the whole read.
 */
export async function fetchShareSnapshot(
  organizationId: string,
  userId: string,
): Promise<ShareSnapshot> {
  const rows: ShareSnapshotRow[] = [];
  let afterId: string | null = null;
  for (;;) {
    let query = supabase
      .from('correspondence_shares')
      .select('id, correspondence_id, permission, email_account_id')
      .eq('organization_id', organizationId)
      .eq('shared_with', userId)
      .is('revoked_at', null);
    if (afterId) {
      query = query.gt('id', afterId);
    }
    const { data, error } = await query.order('id').limit(SNAPSHOT_PAGE_SIZE);
    if (error) {
      throw new Error(error.message);
    }
    for (const { correspondence_id, permission, email_account_id } of data) {
      rows.push({ correspondence_id, permission, email_account_id });
    }
    if (data.length < SNAPSHOT_PAGE_SIZE) {
      return rows;
    }
    afterId = data[data.length - 1].id;
  }
}

/**
 * Correspondence id to the highest permission held. Shares on a shared mailbox
 * confer nothing, so they are left out; both snapshots of a diff are judged
 * against the same accounts cache, so its loading late cannot fake a revoke.
 */
function permissionsByThread(
  queryClient: Pick<QueryClient, 'getQueryData'>,
  organizationId: string,
  rows: ShareSnapshot,
): Map<string, SharePermission> {
  const held = new Map<string, SharePermission>();
  for (const row of rows) {
    if (isSharedAccountId(queryClient, organizationId, row.email_account_id)) {
      continue;
    }
    const current = held.get(row.correspondence_id);
    if (!current || PERMISSION_RANK[row.permission] > PERMISSION_RANK[current]) {
      held.set(row.correspondence_id, row.permission);
    }
  }
  return held;
}

export interface ShareSnapshotDiff {
  readonly revoked: readonly string[];
  readonly downgraded: readonly string[];
}

export function diffShareSnapshots(
  queryClient: Pick<QueryClient, 'getQueryData'>,
  organizationId: string,
  previous: ShareSnapshot,
  next: ShareSnapshot,
): ShareSnapshotDiff {
  const before = permissionsByThread(queryClient, organizationId, previous);
  const after = permissionsByThread(queryClient, organizationId, next);
  const revoked: string[] = [];
  const downgraded: string[] = [];
  for (const [correspondenceId, permission] of before) {
    const now = after.get(correspondenceId);
    if (!now) {
      revoked.push(correspondenceId);
    } else if (PERMISSION_RANK[now] < PERMISSION_RANK[permission]) {
      downgraded.push(correspondenceId);
    }
  }
  return { revoked, downgraded };
}

/**
 * Evicts what a missed revoke left behind, the same way the realtime revoke
 * handler does (repeating it after a delivered event is harmless), and re-reads
 * capabilities and draft history after a downgrade so the thread stays
 * readable but loses what the old role allowed.
 */
export function applyShareSnapshotDiff(
  queryClient: QueryClient,
  organizationId: string,
  diff: ShareSnapshotDiff,
  onRevoked: (correspondenceId: string) => void,
): void {
  for (const correspondenceId of diff.revoked) {
    removeCorrespondenceAccessCaches(queryClient, { correspondenceId, organizationId });
    onRevoked(correspondenceId);
  }
  if (diff.revoked.length > 0) {
    invalidateRevocationListCaches(queryClient, { correspondenceId: undefined, organizationId });
  }

  for (const correspondenceId of diff.downgraded) {
    queryClient
      .invalidateQueries({
        queryKey: correspondenceKeys.capabilities(organizationId, correspondenceId),
        exact: true,
      })
      .catch(() => {
        /* best-effort; the next read re-derives from RLS regardless */
      });
    // Reset, not invalidate: draft bodies the lower role may not read must go
    // even if the refetch fails.
    queryClient
      .resetQueries({
        queryKey: correspondenceKeys.draftHistory(organizationId, correspondenceId),
        exact: true,
      })
      .catch(() => {
        /* best-effort; the next read re-derives from RLS regardless */
      });
  }
}
